import { HOME_STARTUP_SETTLE_LIMIT_MS } from "./home-startup-presentation";

// Content-free, page-load scoped diagnostics. Keep this entry dependency free of
// SDKs: measuring startup must not add another startup network dependency.
export const STARTUP_PHASES = [
  "entry", "app_module_ready", "firebase_auth_resolved", "profile_snapshot",
  "profile_ready", "native_bridge_init", "native_token_received", "auth_ready", "home_rendered", "recent_study_data_ready",
] as const;
type StartupPhase = typeof STARTUP_PHASES[number];
const MAX_DURATION_MS = 120_000;
const NATIVE_FIELDS = [
  "native_js_to_mount_ms", "native_js_to_webview_start_ms", "native_js_to_auth_resolved_ms",
  "native_js_to_bridge_init_ms", "native_js_to_home_ms", "native_token_preparation_ms",
] as const;

export function normalizeStartupSampleRate(value: unknown): number {
  if (value === undefined || value === "") return 0.05;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.05;
}

export function createStartupProfile(now: () => number) {
  const phases: Partial<Record<StartupPhase, number>> = {};
  return {
    mark(phase: StartupPhase): boolean {
      if (phases[phase] !== undefined) return false;
      let value: number;
      try { value = now(); } catch { return false; }
      if (!Number.isFinite(value) || value < 0 || value > MAX_DURATION_MS) return false;
      phases[phase] = Math.round(value);
      return true;
    },
    snapshot(): Record<string, number> {
      return Object.fromEntries(Object.entries(phases).map(([phase, ms]) => [`${phase}_ms`, ms]));
    },
  };
}

export function parseNativeStartupTiming(raw: unknown, startupId: string): Record<string, number> | null {
  if (typeof raw !== "string" || raw.length > 1024) return null;
  try {
    const value = JSON.parse(raw);
    if (value?.version !== 1 || value.startupId !== startupId) return null;
    const result: Record<string, number> = {};
    for (const field of NATIVE_FIELDS) {
      const ms = value[field];
      if (typeof ms === "number" && Number.isFinite(ms) && ms >= 0 && ms <= MAX_DURATION_MS) {
        result[field] = Math.round(ms);
      }
    }
    return result;
  } catch {
    return null;
  }
}

const profile = createStartupProfile(() => performance.now());
let initialized = false;
let enabled = false;
let sampled = false;
let reported = false;
let sampleRate = 0;
let startupId = "";
let signedIn = false;
let platform = "web";
let initialVisibility = "visible";
let nativeTiming: Record<string, number> = {};
let reporter: ((properties: Record<string, unknown>) => void) | undefined;
let pendingReport: Record<string, unknown> | undefined;
let deadline: ReturnType<typeof setTimeout> | undefined;
let readyTimer: ReturnType<typeof setTimeout> | undefined;

function receiveNativeTiming(event: Event) {
  const detail = (event as CustomEvent).detail;
  if (detail?.typeEvent === "init_app" && ["ios", "android"].includes(detail.message)) {
    platform = detail.message;
    markStartupPhase("native_bridge_init");
  }
  if (detail?.typeEvent === "authentication_token") markStartupPhase("native_token_received");
  if (!sampled || detail?.typeEvent !== "startup_timing") return;
  const parsed = parseNativeStartupTiming(detail.message, startupId);
  if (parsed) nativeTiming = parsed;
}

function report(outcome: "ready" | "timeout") {
  if (reported || !sampled) return;
  reported = true;
  clearTimeout(deadline);
  clearTimeout(readyTimer);
  window.removeEventListener("messageFromJungleMobileApp", receiveNativeTiming);
  try {
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    const timings = profile.snapshot();
    const cutoff = timings.home_rendered_ms ?? performance.now();
    const scripts = (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
      .filter(entry => {
        if (entry.startTime > cutoff) return false;
        const url = new URL(entry.name);
        // Browsers report modulepreloads as link, other, or fetch resources.
        // Importing them reuses that transfer; recognize generated JS by URL.
        const isScript = entry.initiatorType === "script" || /\.m?js$/i.test(url.pathname);
        return isScript && url.origin === window.location.origin;
      });
    pendingReport = {
      profile_schema: 1, startup_id: startupId, sample_rate: sampleRate,
      outcome, platform, signed_in: signedIn, initial_visibility: initialVisibility,
      final_visibility: document.visibilityState, still_on_home: window.location.pathname === "/",
      navigation_type: navigation?.type ?? "unknown",
      navigation_response_end_ms: Math.round(navigation?.responseEnd ?? 0),
      startup_script_encoded_bytes: scripts.reduce((sum, entry) => sum + entry.encodedBodySize, 0),
      recent_study_data_ready: timings.recent_study_data_ready_ms !== undefined,
      ...timings, ...nativeTiming,
    };
    flushPendingReport();
  } catch {
    // Neither browser profiling APIs nor reporting failures can affect startup.
  }
}

export function initializeStartupProfiling(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  // Only initial home loads, not every route transition or deep link.
  enabled = window.location.pathname === "/";
  if (!enabled) return;
  initialVisibility = document.visibilityState;
  platform = window.ReactNativeWebView ? "native_unknown" : "web";
  sampleRate = normalizeStartupSampleRate(import.meta.env.VITE_STARTUP_PROFILE_SAMPLE_RATE);
  sampled = Math.random() < sampleRate;
  startupId = globalThis.crypto?.randomUUID?.() ?? `startup-${Math.random().toString(36).slice(2)}`;
  window.addEventListener("messageFromJungleMobileApp", receiveNativeTiming);
  markStartupPhase("entry");
  if (sampled) deadline = setTimeout(() => report("timeout"), 30_000);
}

function flushPendingReport(): void {
  if (!reporter || !pendingReport) return;
  const summary = pendingReport;
  pendingReport = undefined;
  try { reporter(summary); } catch { /* Reporting never blocks startup or retries. */ }
}

export function setStartupProfileReporter(callback: typeof reporter): void {
  reporter = callback;
  // A slow app-module download can exceed the deadline before the existing
  // analytics client is available. Deliver that one summary when it registers.
  flushPendingReport();
}

export function markStartupPhase(phase: StartupPhase): void {
  if (!enabled || reported || !profile.mark(phase)) return;
  try { performance.mark(`jungle:startup:${phase}`); } catch { /* optional browser API */ }
  if (phase === "home_rendered") {
    // Native readiness can wait for the initial home layout. Keep the same
    // single report alive through that ceiling and the existing reply grace.
    const replyGrace = 1000 + (window.ReactNativeWebView ? HOME_STARTUP_SETTLE_LIMIT_MS : 0);
    if (sampled) readyTimer = setTimeout(() => report("ready"), replyGrace);
    else window.removeEventListener("messageFromJungleMobileApp", receiveNativeTiming);
  }
}

export function markStartupAuthenticationReady(isSignedIn: boolean): void {
  if (profile.snapshot().auth_ready_ms !== undefined) return;
  signedIn = isSignedIn;
  markStartupPhase("auth_ready");
}

export function getNativeStartupProfileRequest(): string | undefined {
  return enabled && sampled && !reported ? JSON.stringify({ version: 1, startupId }) : undefined;
}

export function getStartupProfileSnapshot(): Record<string, number> {
  return { ...profile.snapshot(), ...nativeTiming };
}
